import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import HighlightOffIcon from '@mui/icons-material/HighlightOff';
import {
  Box,
  FormControl,
  FormControlLabel,
  FormHelperText,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  TextField,
  Typography,
  useMediaQuery,
} from '@mui/material';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogTitle from '@mui/material/DialogTitle';
import { Field } from 'formik';
import _ from 'lodash';
import React, { Fragment } from 'react';
import { useTranslation } from 'react-i18next';

const withNamespace = (namespace, namespaceIndex, fieldName, parentFieldName) => {
  let namespacedFieldName = `${namespace}.${namespaceIndex}`;

  if (parentFieldName != null) {
    namespacedFieldName += `.${parentFieldName}`;
  }

  namespacedFieldName += `.${fieldName}`;

  return namespacedFieldName;
};

function PanelSubForm({
  namespace,
  panel,
  panelIndex,
  name,
  configuration,
  serviceOptions,
  availableRelays = [],
  removePanel,
  deletePanel,
  undeletePanel,
  canConfirm,
  canUndelete,
  lightFixtures,
  errors,
  touched,
}) {
  const { t } = useTranslation();
  const isDesktop = useMediaQuery(theme => theme.breakpoints.up('lg'));

  const [removeDialogOpen, setRemoveDialogOpen] = React.useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = React.useState(false);

  const handleRemove = () => {
    setRemoveDialogOpen(false);
    removePanel();
  };
  const handleDelete = () => {
    setDeleteDialogOpen(false);
    deletePanel();
  };

  const icon = canConfirm ? (
    _.get(panel, 'exported', false) ? (
      <CheckCircleOutlineIcon
        sx={{ color: 'success.main', ml: '8px' }}
        title={t('projectServices.workForm.exported', 'exported')}
      />
    ) : (
      <HighlightOffIcon
        sx={{ color: 'error.main', ml: '8px' }}
        title={t('projectServices.workForm.notExported', 'not exported')}
      />
    )
  ) : null;

  const isLive = _.get(panel, 'is_live', true);
  const isDeleted = !!panel.delete;

  const namespacedFieldName = (fieldName, parentFieldName) =>
    withNamespace(namespace, panelIndex, fieldName, parentFieldName);

  const visibleSpecifics = configuration.filter(specific => specific.display);
  const hiddenSpecifics = configuration.filter(specific => !specific.display);

  return (
    <Fragment>
      {isDeleted && (
        <Box
          sx={{
            mb: 2,
            p: 2,
            bgcolor: 'error.light',
            border: 1,
            borderColor: 'error.main',
            borderRadius: 'var(--radius-md)',
          }}
        >
          <Typography variant="h6" color="error" sx={{ fontWeight: 'bold' }}>
            {t('projectServices.workForm.panelDeleted', 'This panel is DELETED')}
          </Typography>
          {canUndelete && (
            <Button variant="contained" color="success" onClick={undeletePanel} sx={{ mt: 1 }}>
              {t('projectServices.workForm.undeletePanel', 'Undelete Panel')}
            </Button>
          )}
        </Box>
      )}
      <Box sx={{ opacity: isDeleted ? 0.5 : 1, pointerEvents: isDeleted ? 'none' : 'auto' }}>
        {panel.relay_load ? <Field component="input" type="hidden" name={namespacedFieldName('relay_load')} /> : null}
        {panel.id ? <Field component="input" type="hidden" name={namespacedFieldName('id')} /> : null}
        <Field component="input" type="hidden" name={namespacedFieldName('is_live')} />
        {isLive && !!_.get(panel, 'relay_load', false) ? (
          <Fragment>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', mb: 2 }}>
              <Field name={namespacedFieldName('relay_number')}>
                {({ field, form, meta }) => (
                  <FormControl
                    fullWidth
                    size={isDesktop ? 'small' : 'medium'}
                    required
                    error={meta.touched && Boolean(meta.error)}
                    disabled={isDeleted}
                  >
                    <InputLabel id="relay-number-label">
                      {t('projectServices.workForm.relayNumber', 'Relay Number')}
                    </InputLabel>
                    <Select
                      labelId="relay-number-label"
                      label={t('projectServices.workForm.relayNumber', 'Relay Number')}
                      {...field}
                      value={field.value ?? ''}
                      onChange={e => form.setFieldValue(field.name, e.target.value)}
                      disabled={isDeleted}
                    >
                      {availableRelays.map(relay => (
                        <MenuItem key={relay} value={relay} disabled={isDeleted}>
                          {t('projectServices.workForm.relayHashNum', 'Relay #{{num}}', { num: relay })}
                        </MenuItem>
                      ))}
                    </Select>
                    {meta.touched && meta.error && <FormHelperText>{meta.error}</FormHelperText>}
                  </FormControl>
                )}
              </Field>
              <Box>
                <Button color="warning" onClick={() => setRemoveDialogOpen(true)} sx={{ mr: 1 }} disabled={isDeleted}>
                  {t('projectServices.workForm.removeFromJob', 'Remove from Job')}
                </Button>
                <Button color="error" onClick={() => setDeleteDialogOpen(true)} disabled={isDeleted}>
                  {t('projectServices.workForm.deletePanelBtn', 'Delete Panel')}
                </Button>
              </Box>
            </Box>
            {/* Remove Confirmation Dialog */}
            <Dialog open={removeDialogOpen} onClose={() => setRemoveDialogOpen(false)}>
              <DialogTitle>
                {t('projectServices.workForm.removePanelFromJobTitle', 'Remove Panel from Job?')}
              </DialogTitle>
              <DialogContent>
                <DialogContentText>
                  {t(
                    'projectServices.workForm.removePanelBody',
                    'This will remove the panel from the current job, but the panel itself will not be deleted from the system.',
                  )}
                </DialogContentText>
              </DialogContent>
              <DialogActions>
                <Button onClick={() => setRemoveDialogOpen(false)} color="primary">
                  {t('projectServices.workForm.cancel', 'Cancel')}
                </Button>
                <Button onClick={handleRemove} color="warning" autoFocus>
                  {t('projectServices.workForm.removeFromJob', 'Remove from Job')}
                </Button>
              </DialogActions>
            </Dialog>
            {/* Delete Confirmation Dialog */}
            <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
              <DialogTitle>{t('projectServices.workForm.deletePanelTitle', 'Delete Panel?')}</DialogTitle>
              <DialogContent>
                <DialogContentText>
                  {t(
                    'projectServices.workForm.deletePanelBody',
                    'This will permanently delete the panel from the system. This action cannot be undone. Are you sure you want to continue?',
                  )}
                </DialogContentText>
              </DialogContent>
              <DialogActions>
                <Button onClick={() => setDeleteDialogOpen(false)} color="primary">
                  {t('projectServices.workForm.cancel', 'Cancel')}
                </Button>
                <Button onClick={handleDelete} color="error" autoFocus>
                  {t('projectServices.workForm.deletePanelBtn', 'Delete Panel')}
                </Button>
              </DialogActions>
            </Dialog>
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'row',
                alignItems: 'center',
                justifyContent: 'space-between',
                mb: 1,
              }}
            >
              <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                {panel.panel_name}
              </Typography>
              {icon}
            </Box>
            <Field component="input" type="hidden" name={namespacedFieldName('panel_name')} />
          </Fragment>
        ) : (
          <>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', mb: 2 }}>
              <Stack direction="row" spacing={2} sx={{ flexGrow: 1 }}>
                <Field name={namespacedFieldName('relay_number')}>
                  {({ field, form, meta }) => (
                    <FormControl
                      fullWidth
                      size={isDesktop ? 'small' : 'medium'}
                      required
                      error={meta.touched && Boolean(meta.error)}
                      disabled={isDeleted}
                    >
                      <InputLabel id="relay-number-label">
                        {t('projectServices.workForm.relayNumber', 'Relay Number')}
                      </InputLabel>
                      <Select
                        labelId="relay-number-label"
                        label={t('projectServices.workForm.relayNumber', 'Relay Number')}
                        {...field}
                        value={field.value ?? ''}
                        onChange={e => form.setFieldValue(field.name, e.target.value)}
                        disabled={isDeleted}
                      >
                        {availableRelays.map(relay => (
                          <MenuItem key={relay} value={relay} disabled={isDeleted}>
                            Relay #{relay}
                          </MenuItem>
                        ))}
                      </Select>
                      {meta.touched && meta.error && <FormHelperText>{meta.error}</FormHelperText>}
                    </FormControl>
                  )}
                </Field>
                <Field name={namespacedFieldName('panel_name')}>
                  {({ field, meta }) => (
                    <TextField
                      {...field}
                      required
                      size={isDesktop ? 'small' : 'medium'}
                      label={t('projectServices.workForm.newPanelNameLabel', 'New Panel Name')}
                      fullWidth
                      error={meta.touched && Boolean(meta.error)}
                      helperText={meta.touched && meta.error ? meta.error : ''}
                      disabled={isDeleted}
                    />
                  )}
                </Field>
              </Stack>
              <Box>
                <Button color="warning" onClick={() => setRemoveDialogOpen(true)} sx={{ mr: 1 }} disabled={isDeleted}>
                  {t('projectServices.workForm.removeFromJob', 'Remove from Job')}
                </Button>
                <Button color="error" onClick={() => setDeleteDialogOpen(true)} disabled={isDeleted}>
                  {t('projectServices.workForm.deletePanelBtn', 'Delete Panel')}
                </Button>
              </Box>
            </Box>
            {/* Remove Confirmation Dialog */}
            <Dialog open={removeDialogOpen} onClose={() => setRemoveDialogOpen(false)}>
              <DialogTitle>
                {t('projectServices.workForm.removePanelFromJobTitle', 'Remove Panel from Job?')}
              </DialogTitle>
              <DialogContent>
                <DialogContentText>
                  {t(
                    'projectServices.workForm.removePanelBody',
                    'This will remove the panel from the current job, but the panel itself will not be deleted from the system.',
                  )}
                </DialogContentText>
              </DialogContent>
              <DialogActions>
                <Button onClick={() => setRemoveDialogOpen(false)} color="primary">
                  {t('projectServices.workForm.cancel', 'Cancel')}
                </Button>
                <Button onClick={handleRemove} color="warning" autoFocus>
                  {t('projectServices.workForm.removeFromJob', 'Remove from Job')}
                </Button>
              </DialogActions>
            </Dialog>
            {/* Delete Confirmation Dialog */}
            <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
              <DialogTitle>{t('projectServices.workForm.deletePanelTitle', 'Delete Panel?')}</DialogTitle>
              <DialogContent>
                <DialogContentText>
                  {t(
                    'projectServices.workForm.deletePanelBody',
                    'This will permanently delete the panel from the system. This action cannot be undone. Are you sure you want to continue?',
                  )}
                </DialogContentText>
              </DialogContent>
              <DialogActions>
                <Button onClick={() => setDeleteDialogOpen(false)} color="primary">
                  {t('projectServices.workForm.cancel', 'Cancel')}
                </Button>
                <Button onClick={handleDelete} color="error" autoFocus>
                  {t('projectServices.workForm.deletePanelBtn', 'Delete Panel')}
                </Button>
              </DialogActions>
            </Dialog>
          </>
        )}
        <Grid container spacing={2} sx={{ '& .MuiFormControl-root': { width: '100%' } }}>
          <Grid size={{ lg: 4, md: 6, sm: 12, xs: 12 }}>
            <Field name={namespacedFieldName('board_type')}>
              {({ field, form, meta }) => (
                <FormControl
                  fullWidth
                  size={isDesktop ? 'small' : 'medium'}
                  error={meta.touched && Boolean(meta.error)}
                  disabled={isDeleted}
                >
                  <InputLabel id="board-type-label">{t('projectServices.workForm.panelType', 'Panel Type')}</InputLabel>
                  <Select
                    labelId="board-type-label"
                    label={t('projectServices.workForm.panelType', 'Panel Type')}
                    {...field}
                    value={field.value ?? ''}
                    onChange={e => form.setFieldValue(field.name, e.target.value)}
                    disabled={isDeleted}
                  >
                    <MenuItem value="Bulletin" disabled={isDeleted}>
                      {t('projectServices.workForm.bulletin', 'Bulletin')}
                    </MenuItem>
                    <MenuItem value="Poster" disabled={isDeleted}>
                      {t('projectServices.workForm.poster', 'Poster')}
                    </MenuItem>
                    <MenuItem value="Triface" disabled={isDeleted}>
                      {t('projectServices.workForm.triface', 'Triface')}
                    </MenuItem>
                    <MenuItem value="Digital" disabled={isDeleted}>
                      {t('projectServices.workForm.digital', 'Digital')}
                    </MenuItem>
                  </Select>
                  {meta.touched && meta.error && <FormHelperText>{meta.error}</FormHelperText>}
                </FormControl>
              )}
            </Field>
          </Grid>

          {serviceOptions.length > 0 && (
            <Grid size={{ lg: 4, md: 6, sm: 12, xs: 12 }}>
              <Field name={namespacedFieldName('installation_service')}>
                {({ field, form, meta }) => (
                  <FormControl
                    fullWidth
                    size={isDesktop ? 'small' : 'medium'}
                    error={meta.touched && Boolean(meta.error)}
                    disabled={isDeleted}
                  >
                    <InputLabel id="installation-service-label">
                      {t('projectServices.workForm.service', 'Service')}
                    </InputLabel>
                    <Select
                      labelId="installation-service-label"
                      label={t('projectServices.workForm.service', 'Service')}
                      {...field}
                      value={field.value ?? ''}
                      onChange={e => form.setFieldValue(field.name, e.target.value)}
                      disabled={isDeleted}
                    >
                      {serviceOptions.map(option => (
                        <MenuItem key={option.id} value={option.id} disabled={isDeleted}>
                          {option.label}
                        </MenuItem>
                      ))}
                    </Select>
                    {meta.touched && meta.error && <FormHelperText>{meta.error}</FormHelperText>}
                  </FormControl>
                )}
              </Field>
            </Grid>
          )}

          <Grid size={{ lg: 4, md: 6, sm: 12, xs: 12 }}>
            <Field name={namespacedFieldName('direction')}>
              {({ field, form, meta }) => (
                <FormControl
                  required
                  fullWidth
                  size={isDesktop ? 'small' : 'medium'}
                  error={meta.touched && Boolean(meta.error)}
                  disabled={isDeleted}
                >
                  <InputLabel id="direction-label">{t('projectServices.workForm.direction', 'Direction')}</InputLabel>
                  <Select
                    labelId="direction-label"
                    label={t('projectServices.workForm.direction', 'Direction')}
                    {...field}
                    value={field.value ?? ''}
                    onChange={e => form.setFieldValue(field.name, e.target.value)}
                    disabled={isDeleted}
                  >
                    <MenuItem value="N" disabled={isDeleted}>
                      N
                    </MenuItem>
                    <MenuItem value="N (top)" disabled={isDeleted}>
                      N (top)
                    </MenuItem>
                    <MenuItem value="N (bottom)" disabled={isDeleted}>
                      N (bottom)
                    </MenuItem>
                    <MenuItem value="N (left)" disabled={isDeleted}>
                      N (left)
                    </MenuItem>
                    <MenuItem value="N (right)" disabled={isDeleted}>
                      N (right)
                    </MenuItem>
                    <MenuItem value="S" disabled={isDeleted}>
                      S
                    </MenuItem>
                    <MenuItem value="S (top)" disabled={isDeleted}>
                      S (top)
                    </MenuItem>
                    <MenuItem value="S (bottom)" disabled={isDeleted}>
                      S (bottom)
                    </MenuItem>
                    <MenuItem value="S (left)" disabled={isDeleted}>
                      S (left)
                    </MenuItem>
                    <MenuItem value="S (right)" disabled={isDeleted}>
                      S (right)
                    </MenuItem>
                    <MenuItem value="E" disabled={isDeleted}>
                      E
                    </MenuItem>
                    <MenuItem value="E (top)" disabled={isDeleted}>
                      E (top)
                    </MenuItem>
                    <MenuItem value="E (bottom)" disabled={isDeleted}>
                      E (bottom)
                    </MenuItem>
                    <MenuItem value="E (left)" disabled={isDeleted}>
                      E (left)
                    </MenuItem>
                    <MenuItem value="E (right)" disabled={isDeleted}>
                      E (right)
                    </MenuItem>
                    <MenuItem value="W" disabled={isDeleted}>
                      W
                    </MenuItem>
                    <MenuItem value="W (top)" disabled={isDeleted}>
                      W (top)
                    </MenuItem>
                    <MenuItem value="W (bottom)" disabled={isDeleted}>
                      W (bottom)
                    </MenuItem>
                    <MenuItem value="W (left)" disabled={isDeleted}>
                      W (left)
                    </MenuItem>
                    <MenuItem value="W (right)" disabled={isDeleted}>
                      W (right)
                    </MenuItem>
                  </Select>
                  {meta.touched && meta.error && <FormHelperText>{meta.error}</FormHelperText>}
                </FormControl>
              )}
            </Field>
          </Grid>

          <Grid size={{ lg: 4, md: 6, sm: 12, xs: 12 }}>
            <Field name={namespacedFieldName('ads')}>
              {({ field, meta }) => (
                <TextField
                  {...field}
                  required
                  fullWidth
                  size={isDesktop ? 'small' : 'medium'}
                  label={t('projectServices.workForm.advertisement', 'Advertisement')}
                  error={meta.touched && Boolean(meta.error)}
                  helperText={meta.touched && meta.error ? meta.error : ''}
                  disabled={isDeleted}
                />
              )}
            </Field>
          </Grid>

          <Grid size={{ lg: 4, md: 6, sm: 12, xs: 12 }}>
            <Field name={namespacedFieldName('new_fixtures_per_load')}>
              {({ field, meta }) => (
                <TextField
                  {...field}
                  required
                  fullWidth
                  type="number"
                  size={isDesktop ? 'small' : 'medium'}
                  label={t('projectServices.workForm.newFixtureQty', 'New Fixture Qty')}
                  error={meta.touched && Boolean(meta.error)}
                  helperText={meta.touched && meta.error ? meta.error : ''}
                  disabled={isDeleted}
                />
              )}
            </Field>
          </Grid>

          <Grid size={{ lg: 4, md: 6, sm: 12, xs: 12 }}>
            <Field name={namespacedFieldName('new_fixture_type')}>
              {({ field, form, meta }) => (
                <FormControl
                  fullWidth
                  size={isDesktop ? 'small' : 'medium'}
                  error={meta.touched && Boolean(meta.error)}
                  disabled={isDeleted}
                >
                  <InputLabel>{t('projectServices.workForm.newFixtureType', 'New Fixture Type')}</InputLabel>
                  <Select
                    {...field}
                    label={t('projectServices.workForm.newFixtureType', 'New Fixture Type')}
                    disabled={isDeleted}
                  >
                    {lightFixtures.map(fixture => (
                      <MenuItem key={fixture.id} value={fixture.id} disabled={isDeleted}>
                        {fixture.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              )}
            </Field>
          </Grid>

          {visibleSpecifics.map((specific, index) => (
            <Grid key={`panel-job-info_${index}`} size={{ lg: 4, md: 6, sm: 12, xs: 12 }}>
              <Field name={namespacedFieldName(specific.field, 'specifics')}>
                {({ field, meta }) => (
                  <TextField
                    {...field}
                    required
                    fullWidth
                    size={isDesktop ? 'small' : 'medium'}
                    type={specific.type !== 'int' ? 'text' : 'number'}
                    label={specific.label}
                    error={meta.touched && Boolean(meta.error)}
                    helperText={meta.touched && meta.error ? meta.error : ''}
                    disabled={isDeleted}
                  />
                )}
              </Field>
            </Grid>
          ))}
          {hiddenSpecifics.map((specific, index) => (
            <Field
              key={`panel-job-info-hidden_${index}`}
              component="input"
              type="hidden"
              name={namespacedFieldName(specific.field)}
            />
          ))}
        </Grid>
      </Box>
    </Fragment>
  );
}

export default PanelSubForm;
